import React, { ChangeEvent, useState, useEffect } from "react";
import { RouteProps } from "react-router-dom";
import { connect, ConnectedProps } from "react-redux";
import axios from "axios";
import Config from "config";
import { LightThemeTable } from "components/LightThemeTable";
import Modal from "components/LightThemeModal";
import defaultLogo from "../assets/img/ChainParentDefaultLogo.png";
import doucments from "../assets/img/document.svg";
import { State } from "state";
import {
  addParentChain,
  deleteParentChain,
  fetchParentChains,
  getChainParentLogoSASURL,
  updateParentChain
} from "../state/parentChains";
import { Chain, StateNew } from "interfaces/parentChain";
import Loader from "components/Loader";

const mapChainsForTable = (() => {
  let lastInput: any[] | null = null;
  let lastOutput: any[] = [];

  return (content: any[] = []) => {
    if (content === lastInput) {
      return lastOutput;
    }

    lastInput = content;
    lastOutput = content.map((c) => ({
      id: c.id,
      chainParentName: c.name,
      chainLogo: undefined,
      channel: c.channel,
      numberOfChains: c.chain_count,
      publishedProjects: c.published_projects,
    }));

    return lastOutput;
  };
})();

// Logo display component that fetches logo dynamically
const LogoDisplay: React.FC<{ logo: string | null | undefined; id: number }> = ({ logo, id }) => {
  const [logoUrl, setLogoUrl] = useState<string | null>(logo || null);

  useEffect(() => {
    let cancelled = false;
    const fetchLogo = async () => {
      try {
        if (!logoUrl && typeof id === "number") {
          const resp = await axios.get(`${Config.API_SERVICE_URL}/api/chain-parent-logo/${id}`);
          if (!cancelled && resp && resp.data) {
            const returned = resp.data.logo ?? null;
            if (returned && typeof returned === "string") {
              setLogoUrl(returned.startsWith("http") ? returned : `${Config.API_SERVICE_URL}${returned}`);
            } else {
              setLogoUrl(defaultLogo);
            }
          }
        }
      } catch (e) {
        // Use default logo if API call fails
        if (!cancelled) {
          setLogoUrl(defaultLogo);
        }
      }
    };
    fetchLogo();
    return () => {
      cancelled = true;
    };
  }, [logoUrl, id]);

  return (
    <div
      style={{
        position: "relative",
        textAlign: "center",
        height: "100%",
      }}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt="Chain Logo"
          style={{
            width: 35,
            height: 35,
            objectFit: "contain",
            position: "relative",
            left: "-24px",
          }}
        />
      ) : (
        <span style={{ color: "#999" }}>No Logo</span>
      )}
    </div>
  );
};

// Redux
const mapStateToProps = (state: State) => {
  return {
    chains: mapChainsForTable(state.parentChains.content || []),
    loading: state.parentChains.loading,
    error: state.parentChains.error,
    totalPages: state.parentChains.totalPages,
    currentPage: state.parentChains.currentPage,
    totalItems: state.parentChains.totalItems || 0,
    sasurl:state.parentChains.sasurl
  };
};


const mapDispatch = { fetchParentChains, deleteParentChain, updateParentChain, addParentChain };
const connector = connect(mapStateToProps, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;

type ManageChainParentDataProps = PropsFromRedux &
  RouteProps & {
    style?: React.CSSProperties;
    pageName?: string;
  };

// Styles
const styles = {
  container: { background: "#fff", padding: 10, borderRadius: 10 },
  header: { fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: 14, marginBottom: 15 },
  button: (bg: string, color: string, border: string = "none") => ({
    padding: "5px 15px",
    border,
    borderRadius: 4,
    backgroundColor: bg,
    color,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 600
  }),
  input: { width: "100%", padding: 8, borderRadius: 16, border: "1px solid #ccc", fontSize: 12 },
  dragDrop: (isDragging: boolean) => ({
    border: isDragging ? "2px dashed #E5B42F" : "2px dashed #ccc",
    borderRadius: 6,
    padding: 20,
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    background: isDragging ? "#fff8e1" : "#fafafa",
    textAlign: "left",
  }),
  modalFooter: { display: "flex", justifyContent: "flex-end", marginTop: 15 },
  confirmFooter: { display: "flex", justifyContent: "center", gap: 20 },
};

// Component
class ManageChainParentData extends React.Component<ManageChainParentDataProps, StateNew> {
  constructor(props: ManageChainParentDataProps) {
    super(props);
    this.state = {
      showModal: false,
      newParentName: "",
      newChannel: "",
      newLogoFile: null,
      newLogoPreview: null,
      isDragging: false,
      editingChain: null,
      showStatusModal: false,
      statusMessage: "",
      statusSuccess: false,
      showConfirmModal: false,
      confirmMessage: "",
      onConfirmAction: null,
      currentPage: 1,
      limit: 1000,
      showErrors: false,
      isContinuebtnDisplay: false,
      deletePopoverOpenId: -1,
      showWarningModal: false,
      isAwaitingCreation: false,
      logoError: "",
    };
  }

  componentDidMount() {
    this.props.fetchParentChains(this.state.currentPage, this.state.limit);
  }

  handlePageChange = (newPage: number, newLimit: number = this.state.limit) => {
    this.setState({ currentPage: newPage, limit: newLimit }, () => {
      this.props.fetchParentChains(this.state.currentPage, this.state.limit);
    });
  };

  componentDidUpdate(prevProps: ManageChainParentDataProps) {
    if (prevProps.chains !== this.props.chains && !this.state.editingChain && !this.state.showModal) {
      this.resetForm();
    }
  }

  resetForm = () =>
    this.setState({
      newParentName: "", newChannel: "", newLogoFile: null, newLogoPreview: null,
      editingChain: null,
    });

  fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  handleConfirm = (message: string, onConfirm: () => void) =>
    this.setState({ showConfirmModal: true, confirmMessage: message, onConfirmAction: onConfirm });

  handleDeleteConfirmation = (id: number, row: any) => {
    this.setState({
      deletePopoverOpenId: id,
      isContinuebtnDisplay: true,
      showWarningModal: true,
      statusMessage: `Are you sure you want to delete ${row.chainParentName}?`,
    });
  }
  handleDelete = async (id: number) => {
    try {
      this.setState({
        isAwaitingCreation: true,
      });
      await this.props.deleteParentChain(id).then((response) => {
      this.setState({
      isAwaitingCreation: false,
      showWarningModal: false,
    });
        if (this.props && this.props?.error && this.props?.error.includes("409")) {
          this.setState({
            deletePopoverOpenId: id,
            isContinuebtnDisplay: false,
            showWarningModal: true,
            statusMessage: `This parent chain cannot be deleted because it was already associated with chains.`,
          });
        }
        // this.setState({ isAwaitingCreation: false, showWarningModal: false })
      }).catch((error) => {
        console.log("Error2=>", error)
      });

      await this.props.fetchParentChains(this.state.currentPage, this.state.limit);
    } catch (error) {
      console.log("Error=>", error)
      this.setState({
        showStatusModal: true,
        statusMessage: "Error deleting chain.",
        statusSuccess: false,
      });
    }
  };


  handleEdit = (id: number) => {
    const chain = this.props.chains.find((c) => c.id === id);
    if (!chain) return;
    this.setState({
      editingChain: chain,
      newParentName: chain.chainParentName,
      newChannel: chain.channel || "",
      newLogoPreview: null,
      newLogoFile: null,
      showModal: true,
    }, () => {
      // Logo will be fetched dynamically by LogoDisplay component
    });
  };

  handleExportAll = () => {
    const csv = ["Chain Parent Name,Channel", ...this.props.chains.map((c) => `${c.chainParentName},${c.channel}`)].join("\n");
    const link = document.createElement("a");
    link.href = `data:text/csv;charset=utf-8,${encodeURI(csv)}`;
    link.download = "parent_chains.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  handleCreateNew = () => this.setState({ showModal: true, editingChain: null }, this.resetForm);

  handleSaveNew = async () => {
    const { editingChain, newParentName, newChannel, newLogoFile } = this.state;
    this.setState({
      isAwaitingCreation: true,
    });
    const formData = new FormData();
    formData.append("name", newParentName);
    formData.append("channel", newChannel);

    if (newLogoFile) {
      formData.append("file", newLogoFile);
    }

    try {
      if (editingChain) {
        const result = await this.props.updateParentChain(editingChain.id, formData);
      } else {
        await this.props.addParentChain(formData).then(() => {
          this.setState({ isAwaitingCreation: false, showModal: false, });
        });;
      }

      // Clear state first
      // this.setState({ showModal: false });
      this.resetForm();

      // Force a fresh fetch with a small delay to ensure backend is updated
      setTimeout(async () => {
        await this.props.fetchParentChains(this.state.currentPage, this.state.limit).then(() => {
          this.setState({ isAwaitingCreation: false, showModal: false, });
        });
      }, 500);

    } catch (error: any) {
      console.error('Error response:', error.response?.data);
      let errorMessage = error.response?.status === 409
        ? "Parent chain already exists."
        : `An error occurred: ${error.response?.data || error.message}`;
      this.setState({
        showStatusModal: true,
        showWarningModal: true,
        isContinuebtnDisplay: false,
        isAwaitingCreation: false,
        statusMessage: errorMessage,
        statusSuccess: false
      });
    }
  };
  handleLogoUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg"];

    if (!allowedTypes.includes(file.type)) {
      this.setState({
        showStatusModal: true,
        statusMessage: "Only PNG and JPEG image formats are allowed.",
        statusSuccess: false,
        logoError: "Please upload a valid PNG or JPEG image.",
      });
      return;
    }
    this.setState({ logoError: "" });
    // Revoke old preview URL to free memory
    if (this.state.newLogoPreview && this.state.newLogoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(this.state.newLogoPreview);
    }

    this.setState({
      newLogoFile: file,
      newLogoPreview: URL.createObjectURL(file),
    });
  };

  handleInputChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    this.setState({ [field]: e.target.value } as any);
  };


  handleDrag = (isDragging: boolean) => (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    this.setState({ isDragging });
  };

  handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    this.setState({ isDragging: false });
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg"];

    if (!allowedTypes.includes(file.type)) {
      this.setState({
        showStatusModal: true,
        statusMessage: "Only PNG and JPEG image formats are allowed.",
        statusSuccess: false,
        logoError: "Please upload a valid PNG or JPEG image.",
      });
      return;
    }
    this.setState({ logoError: "" });
    // Revoke old preview URL to free memory
    if (this.state.newLogoPreview && this.state.newLogoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(this.state.newLogoPreview);
    }

    this.setState({
      newLogoFile: file,
      newLogoPreview: URL.createObjectURL(file),
    });
  };

  render() {
    const { chains, loading, error, style } = this.props;
    const allowedTypes = ["image/png", "image/jpeg"];
    const channelOptions = [
      { id: 1, name: "Club" },
      { id: 2, name: "Convenience" },
      { id: 3, name: "Drug" },
      { id: 4, name: "Grocery" },
      { id: 5, name: "Mass" },
      { id: 6, name: "Other" },
      { id: 7, name: "Package Liquor" },
    ];
    const {
      showModal,
      newParentName,
      newChannel,
      newLogoPreview,
      isDragging,
      editingChain,
      showConfirmModal,
      confirmMessage,
      onConfirmAction,
      isAwaitingCreation,
    } = this.state;

    if (error) return <p style={{ color: "red" }}>Error fetching data</p>;

    const columns = [
      {
        field: "chainLogo",
        header: "Chains Logo",
        formatter: (_: any, row: Chain) => (
          <LogoDisplay logo={row.chainLogo} id={row.id} />
        ),
      },
      { field: "chainParentName", header: "Chain Parent" },
      { field: "channel", header: "Channel" },
      { field: "numberOfChains", header: "Number of Chain" },
      { field: "publishedProjects", header: "Published Projects" },
      {
        field: "actions",
        header: "",
        formatter: (_: any, row: Chain) => (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 30 }}>
            <button onClick={() => this.handleEdit(row.id)} style={{ background: "none", border: "none", cursor: "pointer" }}>
              <img src={require("../assets/img/Edit.png")} alt="Edit" />
            </button>
            <button onClick={() => this.handleDeleteConfirmation(row.id, row)} style={{ background: "none", border: "none", cursor: "pointer" }}>
              <img src={require("../assets/img/Delete.png")} alt="Delete" />
            </button>
          </div>
        ),
      },
    ];

    return (
      <div style={{ ...styles.container, ...style }}>
        <h2>Chain Parent</h2>
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 15, gap: 10 }}>
          <button onClick={this.handleExportAll} style={styles.button("transparent", "#E5B42F", "1px solid #E5B42F")}>
            <img src={require("../assets/img/upload.png")} alt="Export Icon" /> Export All Data
          </button>
          <button onClick={this.handleCreateNew} className="primary-button">
            Create New
          </button>
        </div>

        <LightThemeTable
          data={chains}
          columns={columns}
          isLoading={loading}
          defaultPageSize={100}
          pageSizeOptions={[10, 50, 100, 200]}
        />

        {/* Create/Edit Modal */}
        <Modal
          show={showModal}
          onClose={() => this.setState({ showModal: false, showErrors: false, logoError: "" })}
          title={editingChain ? "Edit Parent Data" : "Manage Parent Data"}
          width="800px"
          closeOnBackdropClick={false}
          footer={
            <div style={styles.modalFooter}>
              <div style={{ display: 'flex' }}>
                <div style={{ display: 'flex', marginRight: '5px' }}>
                  {isAwaitingCreation && (<Loader height={20} width={20} custom={true} ></Loader>)}
                </div>
                <button
                  onClick={() => {
                    const { newParentName, newChannel, newLogoFile, editingChain } = this.state;
                    // const hasLogo = !!newLogoFile || (!!editingChain && !!editingChain.chainLogo);
                    // if (!hasLogo) {
                    //   this.setState({
                    //     logoError: "Logo is required. Please upload a PNG or JPEG logo.",
                    //   });
                    //   return;
                    // }
                    this.setState({ logoError: "" });
                    const isValid = newParentName.trim() && newChannel.trim();

                    if (!isValid) {
                      this.setState({ showErrors: true });
                      return;
                    }
                    this.setState({ showErrors: false }, () => {
                      this.handleSaveNew();
                    });
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 4,
                    background: "#E5B42F",
                    color: "#fff",
                    border: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                    minWidth: "72px",
                  }}
                >
                  {!isAwaitingCreation ? 'Submit' : 'Submitting'}
                </button>
              </div>
            </div>
          }
        >
          <div style={{ display: "flex", gap: 20, marginBottom: 15, fontSize: 12 }}>
            <div style={{ flex: 1 }}>
              <label>
                Chain Parent Name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={newParentName}
                onChange={this.handleInputChange("newParentName")}
                placeholder="Enter chain parent name"
                style={{
                  ...styles.input,
                  border: "1px solid #ccc",
                  background: this.state.showErrors && !newParentName ? "#ffe6e6" : "#fff",
                }}
              />
              {this.state.showErrors && !newParentName && (
                <span style={{ color: "red", fontSize: 12 }}>This field is required.</span>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label>
                Channel <span style={{ color: "red" }}>*</span>
              </label>
              <select
                value={newChannel}
                onChange={this.handleInputChange("newChannel")}
                style={{
                  ...styles.input,
                  border: "1px solid #ccc",
                  background: this.state.showErrors && !newChannel ? "#ffe6e6" : "#fff",
                  padding: "10px",
                  color: newChannel ? "#000" : "#999",
                }}
              >
                {!newChannel && (
                  <option value="" disabled>
                    Enter channel name
                  </option>
                )}
                {channelOptions.map((channel) => (
                  <option key={channel.id} value={channel.name}>
                    {channel.name}
                  </option>
                ))}
              </select>
              {this.state.showErrors && !newChannel && (
                <span style={{ color: "red", fontSize: 12 }}>This field is required.</span>
              )}
            </div>
          </div>
          <div
            onDragOver={(e) => { e.preventDefault(); this.handleDrag(true)(e); }}
            onDragLeave={(e) => { e.preventDefault(); this.handleDrag(false)(e); }}
            onDrop={(e) => { e.preventDefault(); this.handleDrop(e); }}
            onClick={() => document.getElementById("new-logo-upload")?.click()}
            style={{
              border: isDragging ? "2px dashed #E5B42F" : "2px dashed #ccc",
              borderRadius: 6,
              padding: 18,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              cursor: "pointer",
              background: isDragging ? "#fff8e1" : "#fafafa",
            }}
          >
            {newLogoPreview ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img
                  src={newLogoPreview}
                  alt="New Logo Preview"
                  style={{ width: 60, height: 60, objectFit: 'contain', border: '1px solid #ddd' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#333", fontSize: 12, fontWeight: 600 }}>
                    New logo selected
                  </div>
                  <div style={{ color: "#666", fontSize: 11 }}>
                    {this.state.newLogoFile?.name}
                  </div>
                  <div style={{ color: "#E5B42F", fontSize: 11, marginTop: 4 }}>
                    Click to change
                  </div>
                </div>
              </div>
            ) : this.state.editingChain?.chainLogo ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img
                  src={`${this.state.editingChain.chainLogo}?t=${Date.now()}`}
                  alt="Current Logo"
                  style={{ width: 60, height: 60, objectFit: 'contain', border: '1px solid #ddd' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#333", fontSize: 12, fontWeight: 600 }}>
                    Current logo
                  </div>
                  <div style={{ color: "#E5B42F", fontSize: 11, marginTop: 4 }}>
                    Click to upload new logo
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img src={doucments} alt="Upload Icon" style={{ width: 24, height: 24 }} />
                <span style={{ color: "#666", fontSize: 12 }}>
                  <span style={{ color: "#E5B42F", fontSize: 12 }}>Click here</span> to upload or drop logo here
                </span>
              </div>
            )}
            <input
              id="new-logo-upload"
              type="file"
              accept="image/png, image/jpeg"
              style={{ display: "none" }}
              onChange={this.handleLogoUpload}
            />
          </div>
          {this.state.logoError && (
            <p style={{ color: "red", fontSize: 12, marginTop: 8 }}>
              {this.state.logoError}
            </p>
          )}
          <p style={{ marginTop: 8, fontSize: 11, color: "#888" }}>
            * Only <strong>PNG</strong> and <strong>JPEG</strong> formats are allowed.
          </p>
        </Modal>

        {/* Confirm Modal */}
        <Modal
          show={showConfirmModal}
          onClose={() => this.setState({ showConfirmModal: false })}
          title=""
          width="400px"
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                paddingTop: 10,
              }}
            >
              <button
                onClick={() => this.setState({ showConfirmModal: false })}
                style={{
                  padding: "6px 16px",
                  borderRadius: 4,
                  border: "none",
                  background: "#E5B42F",
                  color: "#000",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
              {/* <button
                onClick={() =>
                  this.setState({ showConfirmModal: false }, () =>
                    onConfirmAction && onConfirmAction()
                  )
                }
                style={{
                  padding: "6px 16px",
                  borderRadius: 4,
                  border: "none",
                  background: "#E5B42F",
                  color: "#000",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Yes, Continue
              </button> */}
            </div>
          }
        >
          <p style={{ marginBottom: 20, color: "#555", fontSize: 12 }}>
            <strong>{confirmMessage}</strong>
          </p>
        </Modal>
        <div>
          <Modal
            show={this.state.showWarningModal}
            onClose={() => this.setState({ showWarningModal: false,isAwaitingCreation: false })}
            title=""
            width="400px"
            footer={
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  paddingTop: 10,
                }}
              >
                <div style={{ width: '7%', paddingTop: '3px' }}>
                  {isAwaitingCreation && (<Loader height={20} width={20} custom={true} ></Loader>)}
                </div>
                {!this.state.isContinuebtnDisplay && (
                  <button
                    onClick={() => this.setState({ showWarningModal: false, deletePopoverOpenId: -1, isContinuebtnDisplay: false, isAwaitingCreation: false  })}
                    style={{
                      padding: "6px 16px",
                      borderRadius: 4,
                      border: "none",
                      background: "#E5B42F",
                      color: "#000",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    OK
                  </button>
                )}

                {this.state.isContinuebtnDisplay && (
                  <button
                    onClick={() => this.setState({ showWarningModal: false })}
                    style={{
                      padding: "6px 16px",
                      borderRadius: 4,
                      border: "none",
                      background: "#E5B42F",
                      color: "#000",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Cancel
                  </button>
                )}

                {this.state.isContinuebtnDisplay && (
                  <button
                    onClick={() =>
                      this.setState({}, () =>
                        this.handleDelete(this.state.deletePopoverOpenId)
                      )
                    }
                    style={{
                      padding: "6px 16px",
                      borderRadius: 4,
                      border: "none",
                      background: "#E5B42F",
                      color: "#000",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                    disabled={isAwaitingCreation}
                  >
                    Yes, Continue
                  </button>
                )}
              </div>
            }
          >
            <p style={{ marginBottom: 20, color: "#555", fontSize: 12 }}>
              <strong>{this.state.statusMessage}</strong>
            </p>
          </Modal>
        </div>
      </div>
    );
  }
}
export default connector(ManageChainParentData);
